import { Component, DestroyRef, effect, input, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { InlineStatusState } from '../../models/ui/inline-status.type';

@Component({
  selector: 'app-inline-status-indicator',
  templateUrl: './inline-status-indicator.component.html',
  styleUrls: ['./inline-status-indicator.component.scss'],
  standalone: true,
  imports: [FaIconComponent, NgClass]
})
export class InlineStatusIndicatorComponent {
  public readonly state = input<InlineStatusState | InlineStatusState[]>('idle');
  public readonly savingLabel = input('');
  public readonly successLabel = input('');
  public readonly errorLabel = input('erro ao salvar');
  public readonly showSpinner = input(true);
  public readonly autoReset = input(true);
  public readonly autoResetDelay = input(3000);

  public readonly icons = {
    spinner: faSpinner,
    check: faCheck,
    xMark: faCircleXmark
  };

  private readonly _displayState = signal<InlineStatusState>('idle');
  private _autoResetTimeoutId: any = null;

  constructor(private readonly destroyRef: DestroyRef) {
    effect(
      () => {
        const nextState = this._resolveState(this.state());
        const shouldAutoReset = this.autoReset();
        const delay = this.autoResetDelay();

        this._syncDisplayState(nextState, shouldAutoReset, delay);
      },
      { allowSignalWrites: true }
    );

    this.destroyRef.onDestroy(() => this._clearTimeout());
  }

  public get resolvedState(): InlineStatusState {
    return this._displayState();
  }

  public get isVisible(): boolean {
    return this.resolvedState !== 'idle';
  }

  public get text(): string {
    switch (this.resolvedState) {
      case 'saving':
        return this.savingLabel();
      case 'success':
        return this.successLabel();
      case 'error':
        return this.errorLabel();
      default:
        return '';
    }
  }

  public get textClass(): { [klass: string]: boolean } {
    return {
      'text-success': this.resolvedState === 'success',
      'text-danger': this.resolvedState === 'error',
      'text-muted': this.resolvedState === 'saving'
    };
  }

  private _syncDisplayState(nextState: InlineStatusState, shouldAutoReset: boolean, delay: number): void {
    if (nextState === 'saving') {
      this._clearTimeout();
      this._displayState.set('saving');
      return;
    }

    if (nextState === 'success' || nextState === 'error') {
      this._displayState.set(nextState);

      if (shouldAutoReset) {
        this._clearTimeout();
        this._autoResetTimeoutId = setTimeout(() => {
          this._displayState.set('idle');
          this._autoResetTimeoutId = null;
        }, delay);
      }

      return;
    }

    this._clearTimeout();
    this._displayState.set('idle');
  }

  private _resolveState(state: InlineStatusState | InlineStatusState[]): InlineStatusState {
    const states = Array.isArray(state) ? state : [state];

    if (states.some(s => s === 'saving')) {
      return 'saving';
    }

    if (states.some(s => s === 'error')) {
      return 'error';
    }

    if (states.some(s => s === 'success')) {
      return 'success';
    }

    return 'idle';
  }

  private _clearTimeout(): void {
    if (this._autoResetTimeoutId) {
      clearTimeout(this._autoResetTimeoutId);
      this._autoResetTimeoutId = null;
    }
  }
}
